// src/pages/MyReflection/useMyReflection.js
import { useState, useEffect, useCallback } from "react";
import {
  fetchMyReflectionList,
  updateReflection,
  deleteReflection
} from "../../api/my_reflection_api";

export default function useMyReflection() {
  const email = localStorage.getItem("email");

  const [period, setPeriod] = useState("daily");
  const [records, setRecords] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [isEditing, setIsEditing] = useState(false);
  const [editQ1, setEditQ1] = useState([]);
  const [editQ2, setEditQ2] = useState([]);
  const [editQ3, setEditQ3] = useState([]);

  // ── POPUP STATE ──────────────────────────────────────────────────
  const [successPopup, setSuccessPopup] = useState(null);
  const [confirmPopup, setConfirmPopup] = useState(null);

  // ================= LOAD REFLECTIONS =================
  const loadReflections = useCallback(async () => {
    if (!email) return;

    setLoading(true);
    setError("");
    setSelectedCard(null);

    try {
      const data = await fetchMyReflectionList(email, period);

      if (data.success === false) {
        setError(data.message || "No reflections");
        setRecords([]);
      } else {
        setRecords(data.records || []);
      }
    } catch {
      setError("Unable to load reflections");
      setRecords([]);
    }

    setLoading(false);
  }, [email, period]);

  useEffect(() => {
    loadReflections();
  }, [loadReflections]);

  // ================= CARD HANDLING =================
  const openCardDetails = (rec, index) => {
    setSelectedCard({ ...rec, index });
    setIsEditing(false);
  };

  const closeCardDetails = () => {
    setSelectedCard(null);
    setIsEditing(false);
  };

  const startEditing = () => {
    if (!selectedCard) return;
    setIsEditing(true);
    setEditQ1([...(selectedCard.Answers?.Question1?.answers || [])]);
    setEditQ2([...(selectedCard.Answers?.Question2?.answers || [])]);
    setEditQ3([...(selectedCard.Answers?.Question3?.answers || [])]);
  };

  // ================= UPDATE =================
  const saveUpdatedReflection = async () => {
    if (!selectedCard) return;

    const q1 = editQ1.map(a => a.trim()).filter(Boolean).join(" | ");
    const q2 = editQ2.map(a => a.trim()).filter(Boolean).join(" | ");
    const q3 = editQ3.map(a => a.trim()).filter(Boolean).join(" | ");

    try {
      const data = await updateReflection(
        selectedCard.ReflectionID,
        q1,
        q2,
        q3
      );

      if (!data.success) {
        setSuccessPopup({
          type: "error",
          title: "Cannot Update",
          message: data.message || "You cannot update this reflection anymore."
        });
        return;
      }

      setSuccessPopup({
        type: "success",
        title: "Updated!",
        message: "Your reflection has been updated successfully."
      });
      closeCardDetails();
      loadReflections();

    } catch (err) {
      setSuccessPopup({
        type: "error",
        title: "Update Failed",
        message: err.message || "Server Error"
      });
    }
  };

  // ================= DELETE =================
  const deleteReflectionHandler = () => {
    if (!selectedCard) return;

    setConfirmPopup({
      message: "Are you sure you want to delete this reflection? This cannot be undone.",
      onConfirm: async () => {
        setConfirmPopup(null);
        try {
          const data = await deleteReflection(selectedCard.ReflectionID);

          if (data.success === false) {
            setSuccessPopup({
              type: "error",
              title: "Cannot Delete",
              message: data.message || "You cannot delete this reflection (time limit exceeded)."
            });
            return;
          }

          setSuccessPopup({
            type: "success",
            title: "Deleted!",
            message: "Reflection deleted successfully."
          });
          closeCardDetails();
          loadReflections();

        } catch (err) {
          setSuccessPopup({
            type: "error",
            title: "Delete Failed",
            message: err.message || "Unknown error"
          });
        }
      }
    });
  };

  return {
    period, setPeriod,
    records,
    selectedCard,
    loading,
    error,
    isEditing,
    editQ1, setEditQ1,
    editQ2, setEditQ2,
    editQ3, setEditQ3,
    successPopup, setSuccessPopup,
    confirmPopup, setConfirmPopup,
    openCardDetails,
    closeCardDetails,
    startEditing,
    saveUpdatedReflection,
    deleteReflection: deleteReflectionHandler,
  };
}